import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "../../utils/classnames";
import { DURATION, EASE_OUT } from "./motionPresets";

/**
 * Cross-fades two panels that share one slot (the hero booking tabs).
 *
 * The slot is never empty: the outgoing panel is lifted out of the flow and
 * fades/slides away while the incoming one slides in from the side the tab
 * moved, so the two overlap instead of the form blinking out and back.
 * The wrapper animates its own measured height, which keeps whatever sits
 * below it gliding rather than jumping when the panels differ in size.
 *
 * `direction` is 1 when moving to a tab on the right, -1 for one on the left.
 *
 * Nothing is clipped on purpose — the forms hold date pickers and autocomplete
 * dropdowns that have to escape the box, so no overflow-hidden here.
 */
const SwapPanel = ({
  activeKey,
  direction = 1,
  distance = 36,
  className,
  children,
}) => {
  const shouldReduce = useReducedMotion();
  const [height, setHeight] = useState(null);
  const observerRef = useRef(null);

  // Measure the in-flow panel only. The outgoing copy is absolutely positioned
  // while it exits so it can't drag the wrapper's height around as it fades.
  const measureRef = useCallback((node) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    setHeight(node.offsetHeight);
    const observer = new ResizeObserver(() => setHeight(node.offsetHeight));
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  // The height only eases while one panel is replacing another. Any other
  // size change -- a validation message, a loading button, results appearing
  // elsewhere on the page -- is applied instantly: animating height re-lays
  // out the whole page every frame, which made the auto-scroll to the vehicle
  // list stutter.
  const [isSwapping, setIsSwapping] = useState(false);
  const previousKeyRef = useRef(activeKey);
  useEffect(() => {
    if (previousKeyRef.current === activeKey) return;
    previousKeyRef.current = activeKey;
    setIsSwapping(true);
    // A timer rather than onAnimationComplete: when both panels are the same
    // height nothing animates, and the window would never close.
    const swapWindowMs = DURATION.base * 1000 + 100;
    const swapTimer = setTimeout(() => setIsSwapping(false), swapWindowMs);
    return () => clearTimeout(swapTimer);
  }, [activeKey]);

  const shift = shouldReduce ? 0 : distance;
  const duration = shouldReduce ? DURATION.fast : DURATION.base;
  const shouldAnimateHeight = isSwapping && !shouldReduce;

  return (
    <motion.div
      className={cn("tw:relative", className)}
      initial={false}
      animate={{ height: height ?? "auto" }}
      transition={
        shouldAnimateHeight ? { duration, ease: EASE_OUT } : { duration: 0 }
      }
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={activeKey}
          ref={measureRef}
          custom={direction}
          className="tw:w-full tw:top-0 tw:left-0"
          variants={{
            enter: (dir) => ({ opacity: 0, x: dir * shift }),
            center: { opacity: 1, x: 0, position: "relative" },
            exit: (dir) => ({
              opacity: 0,
              x: dir * -shift,
              // Out of the flow (and out of the way of clicks) the moment it
              // starts leaving, so both panels can share the slot.
              position: "absolute",
              pointerEvents: "none",
            }),
          }}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration, ease: EASE_OUT }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default SwapPanel;
